import { ICategory } from '../interfaces/ICategory';
import { getDateTime } from '../helpers/date.helper';

export class CategoryMapper {
  /**
   * Maps category data to select options format
   */
  static toSelect(categories: ICategory[]) {
    return (categories || []).map((category) => ({
      label: category.name,
      value: String(category.id),
    }));
  }

  /**
   * Maps categories to simple list format (id, name only)
   */
  static toList(categories: ICategory[]) {
    return (categories || []).map((category) => ({
      id: category.id,
      name: category.name,
    }));
  }

  /**
   * Maps categories array to object with id as key
   */
  static toObject(categories: ICategory[]) {
    return (categories || []).reduce((acc, category) => {
      acc[category.id] = {
        id: category.id,
        name: category.name,
      };
      return acc;
    }, {} as Record<string, any>);
  }

  /**
   * Maps single category from API to UI format
   */
  static toLocal(category: ICategory): ICategory {
    if (!category) return null;

    return {
      id: category.id,
      name: category.name,
      sku: category.sku || '',
      description: category.description || '',
      isActive: category.isActive ?? category.is_active ?? true,
      parentId: category.parentId || category.parent || null,
      discount: category.discount || 0,
      companyID: category.companyID || category.company_id || null,
      createdAt: category.createdAt 
        ? getDateTime(category.createdAt)
        : undefined,
      updatedAt: category.updatedAt
        ? getDateTime(category.updatedAt)
        : undefined,
    };
  }

  /**
   * Maps categories array to UI format
   */
  static toListUI(categories: ICategory[]): ICategory[] {
    return (categories || []).map((category) => this.toLocal(category));
  }

  /**
   * Maps form data to API create/update request format
   */
  static toCreateRequest(data: any) {
    if (!data) return null;

    return {
      name: data.name,
      sku: data.sku || undefined,
      description: data.description || undefined,
      isActive: data.isActive ?? true,
      parentId: data.parentId || undefined,
      discount: data.discount || undefined,
    };
  }

  /**
   * Maps form data to API update request format
   */
  static toUpdateRequest(id: number, data: any) {
    if (!data) return null;

    return {
      id,
      name: data.name,
      sku: data.sku || undefined,
      description: data.description || undefined,
      isActive: data.isActive ?? true,
      parentId: data.parentId || undefined,
      discount: data.discount || undefined,
    };
  }

  /**
   * Builds a tree structure from flat categories array based on parentId
   */
  static toTree(categories: ICategory[]): ICategory[] {
    if (!categories || !categories.length) return [];

    const categoriesMap = new Map<number, ICategory & { children?: ICategory[] }>();
    const rootCategories: ICategory[] = [];

    // First pass: Create a map of all categories
    categories.forEach((category) => {
      categoriesMap.set(category.id, { ...category, children: [] });
    });

    // Second pass: Build the tree structure
    categories.forEach((category) => {
      const parentId = category.parentId || category.parent;
      const categoryNode = categoriesMap.get(category.id);

      if (!categoryNode) return;

      if (!parentId) {
        // Root level category (no parent)
        rootCategories.push(categoryNode);
      } else {
        // Child category - add to parent's children array
        const parentNode = categoriesMap.get(parentId);
        if (parentNode && parentNode.children) {
          parentNode.children.push(categoryNode);
        } else {
          // If parent not found, treat as root
          rootCategories.push(categoryNode);
        }
      }
    });

    return rootCategories;
  }
}
